import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import MangaCard from '@/components/feature/MangaCard';
import { categories, getMangaByCategory } from '@/mocks/manga';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const mangaList = slug ? getMangaByCategory(slug) : [];

  if (!category) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-20 px-4 md:px-6">
          <div className="max-w-6xl mx-auto text-center py-20">
            <p className="text-foreground-500">分类不存在</p>
            <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm mt-4 inline-block cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const pageTitle = `${category.name} - 免费耽美漫画 | 耽美漫画网`;
  const pageDesc = `${category.name}类耽美漫画免费在线看，耽美漫画网提供最全${category.name}题材免费BL漫画高清在线阅读，每日更新热门${category.name}耽美资源。`;

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-20 pb-12 bg-background-50">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category.name },
            ]}
          />

          <header className="mt-6 mb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">{category.name}漫画</h1>
            <p className="text-sm text-foreground-500 mt-2">{category.description} - {pageDesc.substring(0, 60)}...</p>
          </header>

          <div className="flex flex-wrap gap-2 md:gap-3 mb-8">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-600'
                }`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`${cat.icon} text-xs`}></i>
                </span>
                {cat.name}
              </Link>
            ))}
          </div>

          {mangaList.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
              {mangaList.map((manga) => (
                <MangaCard key={manga.id} manga={manga} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-foreground-500">该分类暂无漫画，敬请期待</p>
            </div>
          )}
        </div>
      </main>
      <Footer />

      <script type="application/ld+json">{JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: pageTitle,
        description: pageDesc,
        url: `https://www.danmeimh.com/category/${slug}`,
      })}</script>

      <HelmetForCategory title={pageTitle} description={pageDesc} slug={slug || ''} />
    </>
  );
}

function HelmetForCategory({ title, description, slug }: { title: string; description: string; slug: string }) {
  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={`耽美漫画,免费耽美漫画,BL漫画,耽美漫画免费看,${slug}`} />
      <link rel="canonical" href={`https://www.danmeimh.com/category/${slug}`} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={`https://www.danmeimh.com/category/${slug}`} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
    </>
  );
}